import { state } from "../lib/state";
import { API_BASE, authenticatedApiRequest } from "../lib/api";
import { showToast, formatError } from "../lib/dom";
import { onScreenChange, setActiveScreen } from "../lib/router";
import { setUserProperties, trackEvent } from "../lib/analytics";
import { renderProjects } from "./closet";
import type {
  BillingCheckoutResponse,
  BillingMeResponse,
  BillingPortalResponse,
  BillingProductsResponse,
  PolarProduct,
  PolarSubscription,
} from "../types/api";

const BILLING_BACKEND_AVAILABLE =
  Boolean(API_BASE) || window.location.protocol === "https:";

const PENDING_CHECKOUT_KEY = "acc_pending_checkout_v1";
const PENDING_CHECKOUT_TTL_MS = 30 * 60 * 1000;

const LAST_CHECKOUT_KEY = "acc_last_checkout_v1";
const LAST_CHECKOUT_TTL_MS = 60 * 60 * 1000;

interface LastCheckoutMarker {
  slug: string;
  productId: string;
  kind: "subscription" | "coins";
  price: string;
  ts: number;
}

function rememberLastCheckout(marker: Omit<LastCheckoutMarker, "ts">): void {
  try {
    localStorage.setItem(
      LAST_CHECKOUT_KEY,
      JSON.stringify({ ...marker, ts: Date.now() }),
    );
  } catch {
    // Best effort.
  }
}

function consumeLastCheckout(): LastCheckoutMarker | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(LAST_CHECKOUT_KEY);
    localStorage.removeItem(LAST_CHECKOUT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as LastCheckoutMarker;
    if (Date.now() - parsed.ts > LAST_CHECKOUT_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

interface PendingCheckout {
  slug: string;
  ts: number;
}

function readPendingCheckout(): PendingCheckout | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PENDING_CHECKOUT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof (parsed as PendingCheckout).slug === "string" &&
      typeof (parsed as PendingCheckout).ts === "number"
    ) {
      const entry = parsed as PendingCheckout;
      if (Date.now() - entry.ts < PENDING_CHECKOUT_TTL_MS) return entry;
    }
  } catch {
    // Fall through.
  }
  try {
    localStorage.removeItem(PENDING_CHECKOUT_KEY);
  } catch {
    // Ignore.
  }
  return null;
}

function clearPendingCheckout(): void {
  try {
    localStorage.removeItem(PENDING_CHECKOUT_KEY);
  } catch {
    // Ignore.
  }
}

export type BillingCycle = "weekly" | "monthly";

interface PriceVariant {
  slug: string;
  price: string;
  credits: number;
}

interface PricingTier {
  id: "premium" | "pro" | "ultra";
  name: string;
  tagline: string;
  recommended?: boolean;
  features: string[];
  weekly: PriceVariant;
  monthly: PriceVariant;
}

interface CoinPackItem {
  slug: string;
  name: string;
  price: string;
  coins: number;
}

export const SUBSCRIPTIONS: PricingTier[] = [
  {
    id: "premium",
    name: "Premium",
    tagline: "Perfect for getting started",
    features: [
      "All outfit & hair models",
      "Watermark-free results",
      "Studio prompts & presets",
      "Priority generation",
    ],
    weekly: { slug: "premium_weekly", price: "$4.99", credits: 120 },
    monthly: { slug: "premium_monthly", price: "$14.99", credits: 480 },
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "Best for everyday creators",
    recommended: true,
    features: [
      "Everything in Premium",
      "Higher weekly credits",
      "Faster queue priority",
      "Save & reuse looks",
      "Early access to new models",
    ],
    weekly: { slug: "pro_weekly", price: "$9.99", credits: 300 },
    monthly: { slug: "pro_monthly", price: "$29.99", credits: 1200 },
  },
  {
    id: "ultra",
    name: "Ultra",
    tagline: "For power users & studios",
    features: [
      "Everything in Pro",
      "Highest credit allocation",
      "Top-tier generation priority",
      "Premium studio quality",
      "Priority support",
    ],
    weekly: { slug: "ultra_weekly", price: "$14.99", credits: 530 },
    monthly: { slug: "ultra_monthly", price: "$44.99", credits: 2120 },
  },
];

export const COIN_PACKS: CoinPackItem[] = [
  { slug: "coins_30", name: "30 Credits", price: "$3.99", coins: 30 },
  { slug: "coins_50", name: "50 Credits", price: "$5.99", coins: 50 },
  { slug: "coins_70", name: "70 Credits", price: "$7.99", coins: 70 },
  { slug: "coins_100", name: "100 Credits", price: "$9.99", coins: 100 },
  { slug: "coins_140", name: "140 Credits", price: "$12.99", coins: 140 },
  { slug: "coins_170", name: "170 Credits", price: "$14.99", coins: 170 },
  { slug: "coins_240", name: "240 Credits", price: "$19.99", coins: 240 },
  { slug: "coins_380", name: "380 Credits", price: "$29.99", coins: 380 },
  { slug: "coins_650", name: "650 Credits", price: "$49.99", coins: 650 },
  { slug: "coins_1200", name: "1200 Credits", price: "$89.99", coins: 1200 },
];

let activeCycle: BillingCycle = "monthly";

function applyCycleUI(): void {
  const buttons = document.querySelectorAll<HTMLElement>("[data-cycle]");
  buttons.forEach(button => {
    const isActive = button.dataset.cycle === activeCycle;
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-selected", isActive ? "true" : "false");
  });
  const grid = document.getElementById("subscription-grid");
  grid?.classList.toggle("is-cycle-weekly", activeCycle === "weekly");
  grid?.classList.toggle("is-cycle-monthly", activeCycle === "monthly");
}

export function inferSlug(product: PolarProduct): string {
  const label = String(product.label || "").toLowerCase();
  if (label.includes("premium weekly")) return "premium_weekly";
  if (label.includes("premium monthly")) return "premium_monthly";
  if (label.includes("pro weekly")) return "pro_weekly";
  if (label.includes("pro monthly")) return "pro_monthly";
  if (label.includes("ultra weekly")) return "ultra_weekly";
  if (label.includes("ultra monthly")) return "ultra_monthly";
  const coinsMatch = label.match(/^(\d+)\s+coins?$/);
  if (coinsMatch && coinsMatch[1]) return `coins_${coinsMatch[1]}`;
  return product.productId;
}

export function productIdForSlug(slug: string): string | null {
  return state.productMap.get(slug) || null;
}

export function buttonLabelForProduct(slug: string): string {
  if (!state.user) return "Subscribe";
  if (!state.billingConfigured) return "Coming soon";
  return productIdForSlug(slug) ? "Subscribe" : "Unavailable";
}

function coinButtonLabel(slug: string): string {
  if (!state.user) return "Buy";
  if (!state.billingConfigured) return "Coming soon";
  return productIdForSlug(slug) ? "Buy" : "Unavailable";
}

function checkoutEnabled(slug: string): boolean {
  if (!state.user) return true;
  return state.billingConfigured && Boolean(productIdForSlug(slug));
}

function variantCredits(variant: PriceVariant): number {
  const productId = productIdForSlug(variant.slug);
  if (productId) {
    const product = state.productDetailsById.get(productId);
    if (product?.credits && product.credits > 0) return product.credits;
  }
  return variant.credits;
}

function titleCase(value: unknown): string {
  return String(value || "")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function deriveTier(activeSubscriptions: PolarSubscription[] = []): string {
  if (!activeSubscriptions.length) return "Free";

  const priority = { ultra: 3, pro: 2, premium: 1 };
  let winner = { name: "Free", rank: 0 };

  activeSubscriptions.forEach(subscription => {
    const product = state.productDetailsById.get(subscription.productId);
    const fallback: PolarProduct = product || {
      productId: subscription.productId,
      label: subscription.productId,
    };
    const rawTier =
      product?.entitlement || product?.label || inferSlug(fallback);
    const normalized = String(rawTier).toLowerCase();
    let tierName = "Free";
    let rank = 0;

    if (normalized.includes("ultra")) {
      tierName = "Ultra";
      rank = priority.ultra;
    } else if (normalized.includes("pro")) {
      tierName = "Pro";
      rank = priority.pro;
    } else if (normalized.includes("premium")) {
      tierName = "Premium";
      rank = priority.premium;
    } else if (product?.entitlement) {
      tierName = titleCase(product.entitlement);
      rank = 1;
    }

    if (rank > winner.rank) {
      winner = { name: tierName, rank };
    }
  });

  return winner.name;
}

function escape(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function createSubscriptionCard(tier: PricingTier): HTMLElement {
  const variant = tier[activeCycle];
  const cadence = activeCycle === "weekly" ? "/week" : "/month";
  const cycleLabel = activeCycle === "weekly" ? "credits / week" : "credits / month";
  const credits = variantCredits(variant);
  const variantProductId = productIdForSlug(variant.slug);
  const isCurrentPlan =
    variantProductId !== null &&
    state.activeSubscriptionProductIds.has(variantProductId);
  const enabled = !isCurrentPlan && checkoutEnabled(variant.slug);

  const article = document.createElement("article");
  article.className = "panel pricing-card";
  // The marketing glow is suppressed for the active plan so the neutral
  // "Current Plan" badge stays visually consistent with its intent.
  if (tier.recommended && !isCurrentPlan) article.classList.add("is-recommended");

  const featuresHtml = tier.features
    .map(item => `<li>${escape(item)}</li>`)
    .join("");

  const badgeHtml = isCurrentPlan
    ? '<span class="current-plan-badge">CURRENT PLAN</span>'
    : tier.recommended
      ? '<span class="recommended-badge">RECOMMENDED</span>'
      : "";

  const buttonLabel = isCurrentPlan
    ? "Current Plan"
    : buttonLabelForProduct(variant.slug);

  article.innerHTML = `
    ${badgeHtml}
    <div class="pricing-card-head">
      <h3>${escape(tier.name)}</h3>
      <p class="tier-tagline">${escape(tier.tagline)}</p>
    </div>
    <div class="price-row">
      <strong>${escape(variant.price)}</strong>
      <span>${cadence}</span>
    </div>
    <span class="credits-chip">
      <img class="coin-icon coin-icon-sm" src="/coin.png" alt="" aria-hidden="true" />
      <span>${credits.toLocaleString()} ${cycleLabel}</span>
    </span>
    <ul class="feature-list">${featuresHtml}</ul>
    <div class="pricing-actions">
      <button class="button ${tier.recommended && !isCurrentPlan ? "button-primary" : "button-secondary"}" type="button" ${enabled ? "" : "disabled"}>
        ${escape(buttonLabel)}
      </button>
    </div>
  `;
  const button = article.querySelector("button");
  button?.addEventListener("click", () => {
    void startCheckout(variant.slug);
  });
  return article;
}

export function createCoinCard(item: CoinPackItem): HTMLElement {
  const enabled = checkoutEnabled(item.slug);
  const article = document.createElement("article");
  article.className = "panel coin-card";
  article.innerHTML = `
    <div class="coin-card-head">
      <img class="coin-icon coin-icon-lg" src="/coin.png" alt="" aria-hidden="true" />
      <span class="coin-amount">${item.coins.toLocaleString()}</span>
    </div>
    <strong class="coin-price">${escape(item.price)}</strong>
    <div class="pricing-actions">
      <button class="button button-secondary" type="button" ${enabled ? "" : "disabled"}>
        ${escape(coinButtonLabel(item.slug))}
      </button>
    </div>
  `;
  const button = article.querySelector("button");
  button?.addEventListener("click", () => {
    void startCheckout(item.slug);
  });
  return article;
}

export function renderPricing(): void {
  const subscriptionGrid = document.getElementById("subscription-grid");
  const coinsGrid = document.getElementById("coins-grid");
  if (!subscriptionGrid || !coinsGrid) return;
  subscriptionGrid.innerHTML = "";
  coinsGrid.innerHTML = "";
  SUBSCRIPTIONS.forEach(tier => subscriptionGrid.appendChild(createSubscriptionCard(tier)));
  COIN_PACKS.forEach(item => coinsGrid.appendChild(createCoinCard(item)));
  applyCycleUI();
}

interface ApplyBillingDataParams {
  credits?: string | number;
  plans?: string;
  billingReady?: boolean;
}

export function applyBillingDataUI(params: ApplyBillingDataParams = {}): void {
  const { credits = 0, plans = "Free", billingReady = false } = params;
  const creditsValue = document.getElementById("credits-value");
  const accountCreditsValue = document.getElementById("account-credits-value");
  const accountPlansValue = document.getElementById("account-plans-value");
  const billingSummaryTitle = document.getElementById("billing-summary-title");
  const portalButton = document.getElementById("portal-button") as HTMLButtonElement | null;

  const creditsText = String(credits);
  if (creditsValue) creditsValue.textContent = creditsText;
  if (accountCreditsValue) accountCreditsValue.textContent = creditsText;
  if (accountPlansValue) accountPlansValue.textContent = String(plans);
  if (billingSummaryTitle) {
    billingSummaryTitle.textContent = billingReady
      ? "Plans & credits"
      : "Pricing coming soon";
  }
  if (portalButton) {
    portalButton.disabled = !state.user || !billingReady;
  }
}

export async function loadProducts(): Promise<void> {
  if (!BILLING_BACKEND_AVAILABLE) {
    state.billingConfigured = false;
    state.productMap = new Map();
    state.productDetailsById = new Map();
    renderPricing();
    return;
  }

  try {
    const data = await authenticatedApiRequest<BillingProductsResponse>("/api/billing/products");
    const products: PolarProduct[] = data?.polar?.products || [];
    state.billingConfigured = Boolean(data?.polar?.configured);
    state.productMap = new Map(
      products.map(product => [product.slug || inferSlug(product), product.productId]),
    );
    state.productDetailsById = new Map(
      products.map(product => [product.productId, product]),
    );
    console.log("Products loaded successfully", {
      productCount: products.length,
      configured: state.billingConfigured,
    });
  } catch (error) {
    console.error("Failed to load products:", error);
    state.billingConfigured = false;
    state.productMap = new Map();
    state.productDetailsById = new Map();

    trackEvent("paywall_failed_to_load_package", {
      description: error instanceof Error ? error.message : String(error),
    });

    if (error instanceof Error && error.message.includes("Authorization")) {
      showToast("Please sign in to view pricing options.", true);
    }
  }
  renderPricing();
}

export async function refreshBillingState(): Promise<void> {
  if (!state.user) {
    state.projects = [];
    state.activeSubscriptionProductIds = new Set();
    renderPricing();
    renderProjects();
    return;
  }

  if (!BILLING_BACKEND_AVAILABLE) {
    state.activeSubscriptionProductIds = new Set();
    applyBillingDataUI({
      credits: 0,
      plans: "Free",
      billingReady: false,
    });
    renderPricing();
    return;
  }

  try {
    const data = await authenticatedApiRequest<BillingMeResponse>("/api/billing/me");
    const activeSubscriptions = data?.polar?.activeSubscriptions || [];
    state.activeSubscriptionProductIds = new Set(
      activeSubscriptions.map(s => s.productId),
    );
    const credits = data?.credits ?? 0;
    const configured = Boolean(data?.polar?.configured);
    const currentTier = deriveTier(activeSubscriptions);

    state.billingConfigured = configured;
    applyBillingDataUI({ credits, plans: currentTier, billingReady: configured });

    setUserProperties({
      credits,
      has_active_subscription: activeSubscriptions.length > 0,
      subscription_tier: currentTier,
      subscription_product_id: activeSubscriptions[0]?.productId,
    });

    console.log("Billing state refreshed", {
      credits,
      tier: currentTier,
      subscriptions: activeSubscriptions.length,
      configured,
    });
  } catch (error) {
    console.error("Failed to load billing state:", error);
    state.activeSubscriptionProductIds = new Set();
    applyBillingDataUI({
      credits: 0,
      plans: "Free",
      billingReady: state.billingConfigured,
    });

    if (error instanceof Error && error.message.includes("Authorization")) {
      showToast("Please sign in to view your account details.", true);
    }
  }

  renderPricing();
}

export async function startCheckout(slug: string): Promise<void> {
  if (!state.user) {
    try {
      localStorage.setItem(
        PENDING_CHECKOUT_KEY,
        JSON.stringify({ slug, ts: Date.now() }),
      );
    } catch {
      // Best effort; if storage is blocked the user just re-clicks after sign-in.
    }
    showToast("Sign in to continue your purchase.");
    setActiveScreen("account");
    return;
  }

  const productId = productIdForSlug(slug);
  if (!productId) {
    showToast("This option isn't available right now. Try again later.", true);
    return;
  }

  const kind: "subscription" | "coins" = slug.startsWith("coins_") ? "coins" : "subscription";
  const price = priceForSlug(slug);

  trackEvent("checkout_initiated", {
    product_slug: slug,
    product_id: productId,
    kind,
    price,
  });

  rememberLastCheckout({ slug, productId, kind, price });

  try {
    const data = await authenticatedApiRequest<BillingCheckoutResponse>(
      "/api/billing/polar/checkout",
      {
        method: "POST",
        body: JSON.stringify({
          productId,
          successUrl: `${window.location.origin}${window.location.pathname}?checkout=success#pricing`,
          cancelUrl: `${window.location.origin}${window.location.pathname}?checkout=cancel#pricing`,
        }),
      },
    );

    if (!data?.checkoutUrl) throw new Error("Checkout URL was not returned.");
    console.log("Checkout created successfully, redirecting...");
    window.location.href = data.checkoutUrl;
  } catch (error: unknown) {
    console.error("Checkout failed:", error);
    showToast(formatError(error), true);
  }
}

function priceForSlug(slug: string): string {
  for (const tier of SUBSCRIPTIONS) {
    if (tier.weekly.slug === slug) return tier.weekly.price;
    if (tier.monthly.slug === slug) return tier.monthly.price;
  }
  const coin = COIN_PACKS.find(pack => pack.slug === slug);
  return coin?.price || "";
}

export async function openPortal(): Promise<void> {
  trackEvent("manage_subscription_tapped");
  try {
    const data = await authenticatedApiRequest<BillingPortalResponse>("/api/billing/polar/portal", {
      method: "POST",
      body: JSON.stringify({
        returnUrl: `${window.location.origin}${window.location.pathname}#pricing`,
      }),
    });
    if (!data?.customerPortalUrl) {
      throw new Error("Customer portal URL was not returned.");
    }
    console.log("Opening customer portal...");
    window.location.href = data.customerPortalUrl;
  } catch (error: unknown) {
    console.error("Portal failed:", error);
    showToast(formatError(error), true);
  }
}

export async function resumePendingCheckout(): Promise<void> {
  if (!state.user) return;
  const pending = readPendingCheckout();
  if (!pending) return;

  clearPendingCheckout();

  if (!state.billingConfigured || !productIdForSlug(pending.slug)) {
    setActiveScreen("pricing");
    return;
  }

  setActiveScreen("pricing");
  await startCheckout(pending.slug);
}

export function reportCheckoutOutcome(outcome: "success" | "cancel"): void {
  const marker = consumeLastCheckout();
  if (outcome === "success") {
    if (marker?.kind === "coins") {
      trackEvent("coin_shop_purchased", {
        product_id: marker.productId,
        product_slug: marker.slug,
        price: marker.price,
      });
    } else {
      trackEvent("paywall_purchased", {
        product_id: marker?.productId,
        product_slug: marker?.slug,
        kind: marker?.kind ?? "subscription",
        price: marker?.price,
      });
    }
  } else {
    trackEvent("checkout_canceled", {
      product_id: marker?.productId,
      product_slug: marker?.slug,
      kind: marker?.kind,
    });
  }
}

export function bind(): void {
  const cycleButtons = document.querySelectorAll<HTMLElement>("[data-cycle]");
  cycleButtons.forEach(button => {
    button.addEventListener("click", () => {
      const next = button.dataset.cycle;
      if (next === "weekly" || next === "monthly") {
        if (next === activeCycle) return;
        activeCycle = next;
        trackEvent("paywall_cycle_changed", { cycle: next });
        renderPricing();
      }
    });
  });
  applyCycleUI();

  let paywallActive = false;
  onScreenChange(screen => {
    if (screen === "pricing") {
      if (!paywallActive) {
        paywallActive = true;
        trackEvent("paywall_shown");
        trackEvent("coin_shop_shown");
      }
    } else if (paywallActive) {
      paywallActive = false;
      trackEvent("paywall_dismissed");
    }
  });
}
